<script setup lang="ts">
import { SellerSizeGuideQuery } from '@gql/SellerSizeGuideQuery'

const t2 = useT2({
  'Body size': 'Kūno matmenys',
  'Product size': 'Prekės matmenys',
  'How to measure?': 'Kaip matuoti?',
  Measurements: 'Matmenys',
})

const props = defineProps<{
  exit: () => void
  guideProductId: string | None
  guideBodyId: string | None
  sellerId: string | None
}>()

function tokey(key: string) {
  return `m:${key}`
}

const KEY_HOW_TO_MEASURE = 'm:how-to-measure'

const data = shallowReactive({
  activeSize: KEY_HOW_TO_MEASURE as string,
  activeUnit: undefined as string | undefined,
  state: 'none' as 'none' | 'body' | 'product',
})

const guideIdId = computed(() => (data.state === 'body' ? props.guideBodyId : data.state === 'product' ? props.guideProductId : undefined))

const { ui: sizeGuide } = useApolloWrap2<gen.SellerSizeGuideQuery, gen.SellerSizeGuideQueryVariables>(SellerSizeGuideQuery)({
  vars() {
    const guideId = guideIdId.value
    const sellerId = props.sellerId
    if (!guideId || !sellerId) return
    return { seller: { _id: sellerId }, guideId }
  },
  ui: (i) => i?.publicSizeGuide,
})

const unitKey = computed(() => data.activeUnit || sizeGuide.value?.units?.at(0)?.key)
const howToMeasure = computed((): boolean => !!sizeGuide.value?.areas?.some((i) => i.description?.value))

type UiCard = {
  key: string
  title: string | None
  areas: {
    key: string
    name: string | None
    title: string | None
    value: string | None
    unit: string | None
  }[]
  instructions?: string | None
  measurments?: string | None
}

const tableAll = computed((): UiCard[] => {
  const { values, areas, sizes } = sizeGuide.value || {}
  const unit = unitKey.value

  const res: UiCard[] = []

  const hasDescription = howToMeasure.value
  const instructions = sizeGuide.value?.instructionsNote?.value
  const measurments = sizeGuide.value?.measurmentsNote?.value

  if (hasDescription || instructions || measurments) {
    res.push({
      title: t2('How to measure?'),
      key: KEY_HOW_TO_MEASURE,
      instructions,
      measurments,
      areas: hasDescription
        ? helpers.arr(areas, (item) => {
            return {
              key: item.key,
              name: item.name,
              title: item.title?.value,
              value: item.description?.value,
              unit: undefined,
            }
          })
        : [],
    })
  }

  const unitTitle = helpers.arrFindId(sizeGuide.value?.units, unit, (i) => i.key)?.unit
  sizes?.forEach((size) => {
    const valuesSize = size ? helpers.arr(values).filter((i) => i.sizeKey === size.key && i.unitKey === unit) : []
    res.push({
      title: size.title?.value,
      key: tokey(size.key),
      areas: helpers.arr(areas, (item) => {
        return {
          key: item.key,
          name: item.name,
          unit: unitTitle,
          title: item.title?.value,
          value: helpers.arrFindId(valuesSize, item.key, (i) => i.areaKey)?.value,
        }
      }),
    })
  })

  return res
})

const carousel = useTemplateRef('carousel')
// const cleanup = useCleanup()
// onMounted(() => {
//   lock(carousel.value, { overflowType: 'clip' })
//   cleanup(() => unlock(carousel.value))
// })
function scrollTo(key: string) {
  data.activeSize = key
  if (key) carousel.value?.scroll({ left: document.getElementById(key)?.offsetLeft, behavior: 'smooth' })
}

const timer = useTimeout()
function scroll() {
  timer.set(() => {
    const scrollLeft = carousel.value?.scrollLeft || 0
    const offsetLeft = carousel.value?.offsetLeft || 0
    const last = (carousel.value?.scrollWidth || 0) - offsetLeft - 40

    if (scrollLeft > last) {
      const lastKey = tableAll.value.at(-1)?.key
      if (lastKey) {
        data.activeSize = lastKey
        return
      }
    }

    let active = { offsetLeft: -1, key: '' }

    for (const item of tableAll.value) {
      const elem = document.getElementById(item.key)
      if (elem) {
        const offsetLeft = elem?.offsetLeft + 40
        if (offsetLeft >= scrollLeft) {
          active = { offsetLeft, key: item.key }
          break
        }
      }
    }

    if (active.key) data.activeSize = active.key
  }, 0)
}

function open() {
  data.state = props.guideProductId ? 'product' : props.guideBodyId ? 'body' : 'none'
}

watch(
  () => tableAll.value,
  () => scroll(),
  { immediate: true }
)
onMounted(() => open())
</script>

<template>
  <div :class="$style.split">
    <div :class="$style.image1">
      <div v-if="guideBodyId && guideProductId" :class="$style.types">
        <ButtonInline grey :open="data.state === 'product'" :txt="t2('Product size')" @click="data.state = 'product'" />
        <ButtonInline grey :open="data.state === 'body'" :txt="t2('Body size')" @click="data.state = 'body'" />
      </div>
      <BaseImage :img="sizeGuide?.image" sizes="420px" fit="center" :no-color="true" :class="$style.image2" />
    </div>

    <div class="_relative _glass bg">
      <div :class="$style.gap" class="_row _wrap _pt04 _pl04 _pr04 _pb1">
        <SizeGuide4Btn v-if="howToMeasure" :on="KEY_HOW_TO_MEASURE === data.activeSize" :txt="t2('How to measure?')" @click="scrollTo(KEY_HOW_TO_MEASURE)" />
        <SizeGuide4Btn v-for="item in sizeGuide?.sizes" :key="tokey(item.key)" :txt="item.title?.value" :on="tokey(item.key) === data.activeSize" @click="scrollTo(tokey(item.key))" />
      </div>

      <SizeGuide4Units :units="sizeGuide?.units" :model-value="unitKey" @update:model-value="(i) => (data.activeUnit = i)" />

      <div ref="carousel" :class="$style.carousel" @scroll.passive="() => scroll()">
        <SizeGuide4Card
          v-for="table2 in tableAll"
          :id="table2.key"
          :key="table2.key"
          :class="$style.carousel1"
          :on="data.activeSize === table2.key"
          :title="table2.title"
          :measurments="table2.measurments"
          :instructions="table2.instructions"
          :areas="table2.areas"
        />
      </div>
    </div>
    <div :class="$style.close" @click="() => exit()">
      <button type="button" :class="$style.close1">
        {{ t2('Close') }}
        <IconExit class="_svg" />
      </button>
    </div>
  </div>
</template>

<style module>
.close {
  composes: global(_sticky);
  composes: global(_ctr);

  composes: global(_mb-1);
  composes: global(_pt08);
  composes: global(_pl04);
  composes: global(_pr04);
  composes: global(_pb04);

  composes: global(_bg5);

  bottom: 0;
  z-index: 99;

  border-radius: 0 0 14px 14px;
  border: 1px solid var(--blue3);
  margin-left: -1px;
}
.close1 {
  composes: global(_ctr);

  composes: global(_h50);
  composes: global(_w100p);
  composes: global(_gap1);
  composes: global(_fs11);
  composes: global(_fw-xl);

  composes: global(_br99);

  background: var(--blue3);
  composes: global(_c0);
  --svg: 18px;
}

.types {
  composes: global(_flexblock);

  composes: global(_absolute);
  composes: global(_pb1);
  composes: global(_pl08);
  composes: global(_gap06);
  composes: global(_wrap);

  bottom: 0;
  background: #ffffffd6;

  z-index: 1;
  left: 0;
}

.split {
  composes: global(_col);
  composes: global(_w100p);
  composes: global(_mt2);

  composes: global(_br14);

  border: 1px solid var(--blue3);
  box-shadow: 0 0 0 5px hsla(240, 100%, 33.9%, 0.1);
}

.gap {
  gap: 4px 0px;
}

.image1 {
  composes: global(_relative);
  composes: global(_ctr);

  composes: global(_pt08);
  composes: global(_pr02);
  composes: global(_pl02);
}
.image2 {
  composes: global(_wm100p);
  max-height: 80vh;
  overflow: visible;
}

.carousel {
  composes: global(_relative);
  composes: global(_w100p);
  composes: global(_flexblock);

  -webkit-font-smoothing: antialiased;
  scroll-behavior: smooth;
  scroll-padding: 0;
  scroll-snap-type: x mandatory;
  -webkit-tap-highlight-color: rgba(0, 0, 0, 0);
  -ms-overflow-style: none;
  scrollbar-width: none;
  -webkit-overflow-scrolling: touch;
  transform: translateZ(0);
  backface-visibility: hidden;
  touch-action: pan-y pan-x pinch-zoom;
  contain: content;
  overflow: scroll;
  border-top: 1px solid var(--blue3);
  overscroll-behavior-x: contain;
  width: calc(100% + 1px);
}
.carousel::-webkit-scrollbar {
  display: none;
}
.carousel1 {
  composes: global(_w100p);
  scroll-snap-align: start;
  -webkit-user-drag: none;
  -webkit-user-select: none;
  -moz-user-select: none;
  user-select: none;
  contain: layout style paint;
  margin: 0;
  border: 1px solid var(--blue3);
  border-width: 0 1px 0 0;
}
</style>
